import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import DarkModeToggle from './DarkModeToggle';
import GlobalSearchModal from './GlobalSearchModal';
import NotificationPanel from './NotificationPanel';
import { useNotifications } from '../../hooks/useNotifications';
import { useState } from 'react';

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const { unreadCount } = useNotifications();

  // ... existing code ...

  return (
    <>
      <header className="sticky top-0 z-50 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
              <i className="ri-book-open-line text-xl sm:text-2xl text-emerald-600 dark:text-emerald-400"></i>
              <span className="text-base sm:text-lg font-bold text-gray-900 dark:text-white whitespace-nowrap">永山の勉強アーカイブ</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {[
                { path: '/', label: 'Top', requiresAuth: false },
                { path: '/dashboard', label: 'ダッシュボード', requiresAuth: false },
                { path: '/diary', label: '日記', requiresAuth: false },
                { path: '/materials', label: '資料', requiresAuth: false },
                { path: '/videos', label: '動画', requiresAuth: false },
                { path: '/quiz', label: 'クイズ', requiresAuth: false },
                { path: '/groups', label: 'グループ', requiresAuth: true },
                { path: '/ranking', label: 'ランキング', requiresAuth: true },
                { path: '/portfolio', label: '研究経過報告', requiresAuth: false },
              ].map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={(e) => {
                    if (link.requiresAuth && !user) {
                      e.preventDefault();
                      navigate('/login');
                    }
                  }}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center space-x-1 ${
                    location.pathname === link.path
                      ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                >
                  <span>{link.label}</span>
                  {link.requiresAuth && !user && (
                    <i className="ri-lock-line text-xs"></i>
                  )}
                </Link>
              ))}
            </nav>

            {/* Right Side Actions */}
            <div className="flex items-center space-x-1 sm:space-x-2">
              <button
                onClick={() => setShowSearch(true)}
                className="flex items-center justify-center w-9 h-9 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                title="検索"
              >
                <i className="ri-search-line text-lg"></i>
              </button>

              <DarkModeToggle />

              {user ? (
                <>
                  {/* 通知ベルボタン */}
                  <div className="relative hidden sm:block">
                    <button
                      data-notif-trigger
                      onClick={() => setShowNotifPanel(v => !v)}
                      className="relative flex items-center justify-center w-9 h-9 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                      title="通知"
                    >
                      <i className="ri-notification-3-line text-lg"></i>
                      {unreadCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center leading-none">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </button>
                    {showNotifPanel && (
                      <NotificationPanel onClose={() => setShowNotifPanel(false)} />
                    )}
                  </div>

                  {/* プロフィールボタン（未読通知があると光る） */}
                  <button
                    onClick={() => { setMobileMenuOpen(false); navigate('/profile'); }}
                    className={`hidden sm:flex items-center space-x-2 px-3 py-2 rounded-lg transition-colors cursor-pointer relative ${
                      unreadCount > 0
                        ? 'bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 ring-2 ring-teal-400/60 ring-offset-1 dark:ring-offset-gray-900 animate-pulse'
                        : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/50'
                    }`}
                    title={unreadCount > 0 ? `未読通知が${unreadCount}件あります` : 'プロフィール'}
                  >
                    <i className="ri-user-line text-lg"></i>
                    <span className="text-sm font-medium whitespace-nowrap">プロフィール</span>
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={async () => {
                      if (signingOut) return;
                      setSigningOut(true);
                      setMobileMenuOpen(false);
                      try { await signOut(); } catch (e) { console.error(e); } finally {
                        setSigningOut(false);
                        navigate('/login', { replace: true });
                      }
                    }}
                    disabled={signingOut}
                    className="hidden sm:flex items-center space-x-2 px-3 py-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer disabled:opacity-50"
                  >
                    {signingOut ? (
                      <i className="ri-loader-4-line text-lg animate-spin"></i>
                    ) : (
                      <i className="ri-logout-box-line text-lg"></i>
                    )}
                    <span className="text-sm font-medium whitespace-nowrap">
                      {signingOut ? '処理中...' : 'ログアウト'}
                    </span>
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="hidden sm:flex items-center space-x-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                >
                  <i className="ri-login-box-line text-lg"></i>
                  <span className="text-sm font-medium whitespace-nowrap">ログイン</span>
                </Link>
              )}

              {/* Mobile Menu Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
              >
                <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden py-4 border-t border-gray-200 dark:border-gray-700">
              <nav className="flex flex-col space-y-1">
                {[
                  { path: '/', label: 'Top', requiresAuth: false },
                  { path: '/dashboard', label: 'ダッシュボード', requiresAuth: false },
                  { path: '/diary', label: '日記', requiresAuth: false },
                  { path: '/materials', label: '資料', requiresAuth: false },
                  { path: '/videos', label: '動画', requiresAuth: false },
                  { path: '/quiz', label: 'クイズ', requiresAuth: false },
                  { path: '/groups', label: 'グループ', requiresAuth: true },
                  { path: '/ranking', label: 'ランキング', requiresAuth: true },
                  { path: '/portfolio', label: '研究経過報告', requiresAuth: false },
                ].map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={(e) => {
                      if (link.requiresAuth && !user) {
                        e.preventDefault();
                        setMobileMenuOpen(false);
                        navigate('/login');
                      } else {
                        setMobileMenuOpen(false);
                      }
                    }}
                    className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors flex items-center justify-between ${
                      location.pathname === link.path
                        ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    <span>{link.label}</span>
                    {link.requiresAuth && !user && (
                      <i className="ri-lock-line text-sm"></i>
                    )}
                  </Link>
                ))}

                {user ? (
                  <>
                    <button
                      onClick={() => { setMobileMenuOpen(false); navigate('/profile'); }}
                      className="px-4 py-3 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center justify-between cursor-pointer text-left w-full"
                    >
                      <div className="flex items-center space-x-2">
                        <i className="ri-user-line text-lg"></i>
                        <span>プロフィール</span>
                      </div>
                      {unreadCount > 0 && (
                        <span className="px-1.5 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">
                          {unreadCount}
                        </span>
                      )}
                    </button>
                    <button
                      onClick={async () => {
                        if (signingOut) return;
                        setSigningOut(true);
                        setMobileMenuOpen(false);
                        try { await signOut(); } catch (e) { console.error(e); } finally {
                          setSigningOut(false);
                          navigate('/login', { replace: true });
                        }
                      }}
                      disabled={signingOut}
                      className="px-4 py-3 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors flex items-center space-x-2 text-left cursor-pointer w-full disabled:opacity-50"
                    >
                      {signingOut ? (
                        <i className="ri-loader-4-line text-lg animate-spin"></i>
                      ) : (
                        <i className="ri-logout-box-line text-lg"></i>
                      )}
                      <span>{signingOut ? '処理中...' : 'ログアウト'}</span>
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-3 rounded-lg text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-700 transition-colors flex items-center space-x-2"
                  >
                    <i className="ri-login-box-line text-lg"></i>
                    <span>ログイン</span>
                  </Link>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {showSearch && <GlobalSearchModal onClose={() => setShowSearch(false)} />}
    </>
  );
}
